import PropTypes from "prop-types";
import React, { Component } from "react";
import { CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import {
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon
} from "@material-ui/icons";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import { resolveAtaAudioPlayback } from "./ataAudioStore";

const CompactRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const FullWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const NativeAudio = styled.audio`
  width: 100%;
  margin-top: 4px;
`;

const ErrorText = styled.span`
  color: #c62828;
  font-size: 13px;
`;

const GhostPlayButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  width: auto;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.color.primary.main};
  background: #fff;
  color: ${({ theme }) => theme.color.primary.dark};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.color.primary.dark};
    border-color: ${({ theme }) => theme.color.primary.dark};
    color: #fff;
  }
`;

const SampleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 30px;
  min-width: 58px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.color.primary.main};
  background: #fff;
  color: ${({ theme }) => theme.color.primary.dark};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  cursor: pointer;

  svg {
    flex: 0 0 auto;
    font-size: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.color.primary.light};
  }
`;

class AtaAudioPlayer extends Component {
  constructor(props) {
    super(props);
    this.audioRef = React.createRef();
    this.sampleTimer = null;
    this.clipRaf = null;
    this.clipStopOnce = false;
    // Cancela seek assíncrono antigo quando o usuário toca/pausa de novo
    this.seekToken = 0;
    // Seek com play de fragmento: não emitir ata-audio-user-play (continuidade)
    this.suppressUserPlayEvent = false;
    this.clipInstanceId = `clip_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    this.state = {
      loading: true,
      playing: false,
      error: null,
      objectUrl: null,
      shouldRevoke: false
    };
  }

  componentDidMount() {
    this.loadAudio();
    this.bindAudioEvents();
  }

  componentDidUpdate(prevProps) {
    const { ataId, meetingId, sampleStartSec, sampleEndSec } = this.props;
    if (prevProps.ataId !== ataId || prevProps.meetingId !== meetingId) {
      this.revokeUrl();
      this.setState(
        {
          loading: true,
          playing: false,
          error: null,
          objectUrl: null,
          shouldRevoke: false
        },
        () => this.loadAudio()
      );
      return;
    }
    // Troca de trecho: para o clipe anterior e limpa o timer
    if (
      prevProps.sampleStartSec !== sampleStartSec ||
      prevProps.sampleEndSec !== sampleEndSec
    ) {
      this.clearSampleTimer();
      const { playing } = this.state;
      if (playing) {
        this.pauseAudio();
      }
    }
  }

  componentWillUnmount() {
    this.unbindAudioEvents();
    this.clearSampleTimer();
    this.clearClipRaf();
    const audio = this.audioRef.current;
    if (audio) {
      try {
        audio.pause();
      } catch (err) {
        /* ignore */
      }
    }
    this.revokeUrl();
  }

  clipDebug = (...args) => {
    if (typeof window === "undefined") return;
    // Sempre loga; filtre no DevTools por "[ata-clip]"
    // Desliga com: window.__ATA_CLIP_DEBUG__ = false
    // eslint-disable-next-line no-underscore-dangle
    if (window.__ATA_CLIP_DEBUG__ === false) return;
    // eslint-disable-next-line no-console
    console.info("[ata-clip]", ...args);
  };

  clearClipRaf = () => {
    if (this.clipRaf != null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.clipRaf);
    }
    this.clipRaf = null;
  };

  clearSampleTimer = () => {
    if (this.sampleTimer) {
      clearTimeout(this.sampleTimer);
      this.sampleTimer = null;
    }
  };

  pauseAudio = () => {
    const audio = this.audioRef.current;
    if (!audio) return;
    this.clearSampleTimer();
    this.clearClipRaf();
    audio.pause();
    this.setState({ playing: false });
    this.emitTime();
  };

  /** Limite efetivo do clipe (props; fallback curto se end inválido). */
  getClipEndSec = () => {
    const { sampleStartSec, sampleEndSec } = this.props;
    const start = Number(sampleStartSec || 0);
    const end = Number(sampleEndSec);
    if (Number.isFinite(end) && end > start + 0.05) return end;
    return start + 8;
  };

  /** Pausa o clipe no fim do trecho e volta ao início (replay limpo). */
  stopClipAtEnd = (reason = "end") => {
    if (this.clipStopOnce) return;
    this.clipStopOnce = true;
    const audio = this.audioRef.current;
    const { sampleStartSec, onClipStop } = this.props;
    const start = Number(sampleStartSec || 0);
    const end = this.getClipEndSec();
    const t = audio ? Number(audio.currentTime) || 0 : null;
    this.clipDebug("STOP", {
      reason,
      t,
      start,
      end,
      id: this.clipInstanceId
    });
    this.clearSampleTimer();
    this.clearClipRaf();
    if (audio) {
      audio.pause();
      try {
        audio.currentTime = start;
      } catch (err) {
        /* ignore */
      }
    }
    this.setState({ playing: false });
    this.emitTime();
    if (typeof onClipStop === "function") {
      onClipStop({
        startSec: start,
        endSec: end,
        reason
      });
    }
  };

  /** Fim do trecho (timer, rAF ou timeupdate). */
  enforceClipBounds = () => {
    if (!this.isClipPlayer()) return false;
    const audio = this.audioRef.current;
    if (!audio || audio.paused) return false;
    const { sampleStartSec } = this.props;
    const start = Number(sampleStartSec || 0);
    const end = this.getClipEndSec();
    if (!(end > start)) return false;
    const t = Number(audio.currentTime) || 0;
    if (t >= end - 0.08) {
      this.stopClipAtEnd("bounds");
      return true;
    }
    return false;
  };

  /**
   * Para no fim com rAF + timeout de segurança.
   * (setTimeout único falhava se o seek atrasasse o currentTime)
   */
  scheduleClipEnd = () => {
    this.clearSampleTimer();
    this.clearClipRaf();
    this.clipStopOnce = false;
    if (!this.isClipPlayer()) return;
    const { sampleStartSec } = this.props;
    const start = Number(sampleStartSec || 0);
    const end = this.getClipEndSec();
    if (!(end > start)) {
      this.clipDebug("schedule SKIP invalid bounds", { start, end });
      return;
    }
    this.clipDebug("schedule end watch", {
      start,
      end,
      dur: end - start,
      id: this.clipInstanceId
    });

    const tick = () => {
      if (this.enforceClipBounds()) return;
      const audio = this.audioRef.current;
      if (!audio || audio.paused) {
        this.clipRaf = null;
        return;
      }
      this.clipRaf = requestAnimationFrame(tick);
    };
    this.clipRaf = requestAnimationFrame(tick);

    // Segurança absoluta: mesmo se rAF falhar, corta no tempo do trecho + margem
    const ms = Math.max(120, (end - start) * 1000 + 200);
    this.sampleTimer = setTimeout(() => {
      const audio = this.audioRef.current;
      if (audio && !audio.paused) {
        this.clipDebug("SAFETY timeout → stop", {
          t: audio.currentTime,
          end
        });
        this.stopClipAtEnd("safety-timeout");
      }
    }, ms);
  };

  bindAudioEvents = () => {
    this.unbindAudioEvents();
    if (typeof window === "undefined") return;

    // Clipes da transcrição: pausam quando a reunião toca (ou outro clip)
    if (!this.listensToGlobalAudioEvents()) {
      this.onClipPauseEvent = event => {
        const { ataId } = this.props;
        const detail = (event && event.detail) || {};
        if (detail.ataId && detail.ataId !== ataId) return;
        if (detail.exceptId && detail.exceptId === this.clipInstanceId) {
          return;
        }
        this.pauseAudio();
      };
      window.addEventListener("ata-clip-pause", this.onClipPauseEvent);
      return;
    }

    this.onSeekEvent = event => {
      const { ataId } = this.props;
      const detail = (event && event.detail) || {};
      if (detail.ataId && detail.ataId !== ataId) return;
      if (!Number.isFinite(detail.time)) return;
      this.seekTo(detail.time, {
        play: detail.play === true,
        fragment: detail.fragment === true
      });
    };
    this.onPauseEvent = event => {
      const { ataId } = this.props;
      const detail = (event && event.detail) || {};
      if (detail.ataId && detail.ataId !== ataId) return;
      this.pauseAudio();
    };
    this.onToggleEvent = event => {
      const { ataId } = this.props;
      const detail = (event && event.detail) || {};
      if (detail.ataId && detail.ataId !== ataId) return;
      this.togglePlay();
    };
    window.addEventListener("ata-audio-seek", this.onSeekEvent);
    window.addEventListener("ata-audio-pause", this.onPauseEvent);
    window.addEventListener("ata-audio-toggle", this.onToggleEvent);
  };

  unbindAudioEvents = () => {
    if (typeof window === "undefined") return;
    if (this.onSeekEvent) {
      window.removeEventListener("ata-audio-seek", this.onSeekEvent);
      this.onSeekEvent = null;
    }
    if (this.onPauseEvent) {
      window.removeEventListener("ata-audio-pause", this.onPauseEvent);
      this.onPauseEvent = null;
    }
    if (this.onToggleEvent) {
      window.removeEventListener("ata-audio-toggle", this.onToggleEvent);
      this.onToggleEvent = null;
    }
    if (this.onClipPauseEvent) {
      window.removeEventListener("ata-clip-pause", this.onClipPauseEvent);
      this.onClipPauseEvent = null;
    }
  };

  listensToGlobalAudioEvents = () => {
    const { variant, listenGlobalEvents } = this.props;
    if (listenGlobalEvents === false) return false;
    if (listenGlobalEvents === true) return true;
    // Só o player principal da reunião escuta seek/pause/toggle globais
    return variant === "full";
  };

  /** Player de trecho (transcrição / amostra), independente do áudio da reunião. */
  isClipPlayer = () => {
    const { sampleStartSec, variant } = this.props;
    return (
      !this.listensToGlobalAudioEvents() &&
      (sampleStartSec != null || variant === "sample")
    );
  };

  waitForSeek = (mediaEl, target, token) =>
    new Promise(resolve => {
      let settled = false;
      let onSeeked = null;
      const finish = ok => {
        if (settled) return;
        settled = true;
        if (onSeeked) mediaEl.removeEventListener("seeked", onSeeked);
        resolve(ok !== false);
      };
      onSeeked = () => {
        const cur = Number(mediaEl.currentTime) || 0;
        finish(Math.abs(cur - target) <= 0.75);
      };
      mediaEl.addEventListener("seeked", onSeeked);
      const trySet = () => {
        try {
          // currentTime é a API nativa do <audio>
          // eslint-disable-next-line no-param-reassign
          mediaEl.currentTime = target;
        } catch (err) {
          finish(false);
        }
      };
      trySet();
      // Já no alvo (alguns browsers não disparam seeked)
      if (Math.abs((Number(mediaEl.currentTime) || 0) - target) <= 0.35) {
        finish(true);
        return;
      }
      // Retry + timeout: só aceita se o tempo realmente chegou perto do alvo
      window.setTimeout(() => {
        if (token !== this.seekToken) {
          finish(false);
          return;
        }
        if (Math.abs((Number(mediaEl.currentTime) || 0) - target) <= 0.75) {
          finish(true);
          return;
        }
        trySet();
        window.setTimeout(() => {
          if (token !== this.seekToken) {
            finish(false);
            return;
          }
          const cur = Number(mediaEl.currentTime) || 0;
          finish(Math.abs(cur - target) <= 0.75);
        }, 250);
      }, 350);
    });

  seekTo = async (timeSec, opts = {}) => {
    const audio = this.audioRef.current;
    if (!audio || !Number.isFinite(timeSec)) return;
    const shouldPlay = opts.play === true;
    const target = Math.max(0, Number(timeSec) || 0);
    this.seekToken += 1;
    const token = this.seekToken;
    try {
      this.clearSampleTimer();
      // Garante metadata antes do seek (evita falha silenciosa)
      if (audio.readyState < 1) {
        await new Promise(resolve => {
          const done = () => {
            audio.removeEventListener("loadedmetadata", done);
            resolve();
          };
          audio.addEventListener("loadedmetadata", done);
        });
      }
      // Usuário pediu play/pause enquanto o seek esperava metadata
      if (token !== this.seekToken) return;
      audio.pause();
      // currentTime é assíncrono — só toca depois de confirmar o pouso
      const landed = await this.waitForSeek(audio, target, token);
      if (token !== this.seekToken) return;
      if (!landed) {
        // Não tocar no trecho errado: reposiciona o estado no tempo real
        this.setState({ playing: false });
        this.emitTime();
        return;
      }
      if (!shouldPlay) {
        this.setState({ playing: false });
        this.emitTime();
        return;
      }
      // Play via seek do card de confirmação: não ligar continuidade
      if (opts.fragment === true) {
        this.suppressUserPlayEvent = true;
      }
      await audio.play();
      if (token !== this.seekToken) return;
      this.setState({ playing: true });
      this.emitTime();
    } catch (e) {
      this.suppressUserPlayEvent = false;
      if (token !== this.seekToken) return;
      // autoplay bloqueado / seek inválido: só posiciona
      try {
        await this.waitForSeek(audio, target, token);
        if (token !== this.seekToken) return;
        audio.pause();
        this.setState({ playing: false });
        this.emitTime();
      } catch (err) {
        /* ignore */
      }
    }
  };

  revokeUrl = () => {
    const { objectUrl, shouldRevoke } = this.state;
    if (objectUrl && shouldRevoke) {
      URL.revokeObjectURL(objectUrl);
    }
  };

  loadAudio = async () => {
    const { ataId, meetingId, fileName, onUnavailable, t } = this.props;
    try {
      const result = await resolveAtaAudioPlayback({
        id: ataId,
        meetingId,
        audioFileName: fileName
      });
      if (!result || !result.url) {
        this.setState({
          loading: false,
          error: t("smartMinutes.audio.unavailable")
        });
        if (onUnavailable) onUnavailable();
        return;
      }
      this.setState({
        loading: false,
        objectUrl: result.url,
        shouldRevoke: result.revoke !== false,
        error: null
      });
    } catch (e) {
      this.setState({
        loading: false,
        error: t("smartMinutes.audio.loadError")
      });
      if (onUnavailable) onUnavailable();
    }
  };

  togglePlay = async event => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const audio = this.audioRef.current;
    if (!audio) return;
    const { ataId, sampleStartSec, onClipPlay } = this.props;
    // Invalida seek pendente (ex.: pausa fala a fala) para não matar o play
    this.seekToken += 1;
    const token = this.seekToken;
    try {
      if (audio.paused) {
        this.clearSampleTimer();
        this.clearClipRaf();
        this.clipStopOnce = false;
        // Clipe: sempre do início do trecho (permite ouvir de novo após o fim)
        if (this.isClipPlayer() && sampleStartSec != null) {
          const start = Number(sampleStartSec) || 0;
          const end = this.getClipEndSec();
          this.clipDebug("PLAY start", {
            start,
            end,
            dur: end - start,
            id: this.clipInstanceId,
            isClip: true
          });
          try {
            audio.currentTime = start;
          } catch (err) {
            /* ignore */
          }
          // Espera o seek pousar antes de tocar/agendar o fim
          await this.waitForSeek(audio, start, token);
          if (token !== this.seekToken) return;
        } else if (sampleStartSec != null) {
          audio.currentTime = Number(sampleStartSec) || 0;
        }
        if (typeof window !== "undefined") {
          if (this.isClipPlayer()) {
            // Pausa reunião e outros clipes — este player fica independente
            window.dispatchEvent(
              new CustomEvent("ata-audio-pause", { detail: { ataId } })
            );
            window.dispatchEvent(
              new CustomEvent("ata-clip-pause", {
                detail: { ataId, exceptId: this.clipInstanceId }
              })
            );
            if (typeof onClipPlay === "function") {
              onClipPlay({
                startSec: Number(sampleStartSec || 0),
                endSec: this.getClipEndSec()
              });
            }
          } else if (this.listensToGlobalAudioEvents()) {
            window.dispatchEvent(
              new CustomEvent("ata-clip-pause", { detail: { ataId } })
            );
            window.dispatchEvent(
              new CustomEvent("ata-audio-user-play", {
                detail: { ataId, time: Number(audio.currentTime) || 0 }
              })
            );
          }
        }
        await audio.play();
        if (token !== this.seekToken) return;
        this.setState({ playing: true });
        this.emitTime();
        if (this.isClipPlayer()) {
          this.scheduleClipEnd();
        }
      } else {
        this.clipDebug("PLAY pause by user", { id: this.clipInstanceId });
        this.clearSampleTimer();
        this.clearClipRaf();
        audio.pause();
        this.setState({ playing: false });
        this.emitTime();
      }
    } catch (e) {
      const { t } = this.props;
      this.clipDebug("PLAY error", e);
      this.setState({ error: t("smartMinutes.audio.playError") });
    }
  };

  emitTime = () => {
    const { onTimeUpdate } = this.props;
    if (!onTimeUpdate) return;
    const audio = this.audioRef.current;
    if (!audio) return;
    onTimeUpdate(Number(audio.currentTime) || 0, {
      playing: !audio.paused,
      duration: Number(audio.duration) || null
    });
  };

  onEnded = () => {
    this.clearSampleTimer();
    this.clearClipRaf();
    if (this.isClipPlayer()) {
      this.stopClipAtEnd("audio-ended");
      return;
    }
    this.setState({ playing: false });
    this.emitTime();
  };

  onPause = () => {
    const audio = this.audioRef.current;
    if (audio && audio.paused) {
      this.setState({ playing: false });
    }
    this.emitTime();
  };

  onPlay = () => {
    this.setState({ playing: true });
    this.emitTime();
    if (typeof window === "undefined") return;
    const { ataId } = this.props;
    // Player nativo da reunião: pausa clipes da transcrição
    if (!this.listensToGlobalAudioEvents()) return;
    window.dispatchEvent(
      new CustomEvent("ata-clip-pause", { detail: { ataId } })
    );
    // Seek do fragmento (confirmação): não liga continuidade
    if (this.suppressUserPlayEvent) {
      this.suppressUserPlayEvent = false;
      return;
    }
    const audio = this.audioRef.current;
    window.dispatchEvent(
      new CustomEvent("ata-audio-user-play", {
        detail: {
          ataId,
          time: audio ? Number(audio.currentTime) || 0 : 0
        }
      })
    );
  };

  onTimeUpdate = () => {
    this.emitTime();
    this.enforceClipBounds();
  };

  renderHiddenAudio = objectUrl => (
    // Áudio de reunião / amostra — sem legendas disponíveis neste fluxo.
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <audio
      ref={this.audioRef}
      src={objectUrl}
      preload="metadata"
      onEnded={this.onEnded}
      onPause={this.onPause}
      onPlay={this.onPlay}
      onTimeUpdate={this.onTimeUpdate}
      onSeeked={this.emitTime}
      style={{ display: "none" }}
    />
  );

  render() {
    const { variant, fileName, label, t } = this.props;
    const { loading, playing, error, objectUrl } = this.state;
    const playLabel = label || t("smartMinutes.audio.listen");

    if (loading) {
      return (
        <CompactRow>
          <CircularProgress size={18} />
        </CompactRow>
      );
    }

    if (error || !objectUrl) {
      if (variant === "full") {
        return (
          <ErrorText>{error || t("smartMinutes.audio.unavailable")}</ErrorText>
        );
      }
      return null;
    }

    if (variant === "full") {
      return (
        <FullWrap>
          {fileName && (
            <span style={{ color: "#607681", fontSize: 13 }}>{fileName}</span>
          )}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <NativeAudio
            ref={this.audioRef}
            src={objectUrl}
            controls
            preload="metadata"
            onEnded={this.onEnded}
            onPause={this.onPause}
            onPlay={this.onPlay}
            onTimeUpdate={this.onTimeUpdate}
            onSeeked={this.emitTime}
          >
            {t("smartMinutes.audio.unsupported")}
          </NativeAudio>
        </FullWrap>
      );
    }

    if (variant === "ghost") {
      return (
        <CompactRow>
          {this.renderHiddenAudio(objectUrl)}
          <GhostPlayButton
            type="button"
            onClick={this.togglePlay}
            aria-label={playing ? t("smartMinutes.audio.pause") : playLabel}
          >
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
            {playing ? t("smartMinutes.audio.pauseShort") : playLabel}
          </GhostPlayButton>
        </CompactRow>
      );
    }

    if (variant === "sample") {
      return (
        <CompactRow>
          {this.renderHiddenAudio(objectUrl)}
          <SampleButton
            type="button"
            onClick={this.togglePlay}
            aria-label={
              playing
                ? `${t("smartMinutes.audio.pauseShort")} ${playLabel}`
                : `${t("smartMinutes.audio.listen")} ${playLabel}`
            }
            title={
              playing
                ? t("smartMinutes.audio.pauseShort")
                : `${t("smartMinutes.audio.listen")} ${playLabel}`
            }
          >
            {playing ? (
              <PauseIcon fontSize="small" />
            ) : (
              <PlayArrowIcon fontSize="small" />
            )}
            {playLabel}
          </SampleButton>
        </CompactRow>
      );
    }

    return (
      <CompactRow>
        {this.renderHiddenAudio(objectUrl)}
        <Tooltip
          title={
            playing
              ? t("smartMinutes.audio.pause")
              : t("smartMinutes.audio.play")
          }
        >
          <IconButton
            size="small"
            color="primary"
            onClick={this.togglePlay}
            aria-label={
              playing
                ? t("smartMinutes.audio.pause")
                : t("smartMinutes.audio.play")
            }
          >
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>
      </CompactRow>
    );
  }
}

AtaAudioPlayer.propTypes = {
  ataId: PropTypes.string.isRequired,
  meetingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fileName: PropTypes.string,
  label: PropTypes.string,
  variant: PropTypes.oneOf(["compact", "full", "ghost", "sample"]),
  sampleStartSec: PropTypes.number,
  sampleEndSec: PropTypes.number,
  /** false: ignora eventos globais (amostras). Default: só variant full. */
  listenGlobalEvents: PropTypes.bool,
  onClipPlay: PropTypes.func,
  onClipStop: PropTypes.func,
  onUnavailable: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  t: PropTypes.func.isRequired
};

AtaAudioPlayer.defaultProps = {
  meetingId: null,
  fileName: null,
  label: null,
  variant: "compact",
  sampleStartSec: null,
  sampleEndSec: null,
  listenGlobalEvents: undefined,
  onClipPlay: null,
  onClipStop: null,
  onUnavailable: null,
  onTimeUpdate: null
};

export default withTranslation()(AtaAudioPlayer);
